import React, { useState, useCallback, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  CloudUpload,
  CheckCircle,
  Visibility,
  Description,
  Clear,
  Download as DownloadIcon,
  Label,
  Assignment,
  Factory,
  QrCode
} from '@mui/icons-material';
import {
  FaAmazon,
  FaIndustry,
  FaCut
} from 'react-icons/fa';
import { NotificationContext } from '../context/NotificationContext';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';

const AmazonLabelSeparator = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const {
    addActivityProcess,
    updateActivityProcess,
    addLog,
    completeActivityProcess,
  } = useActivityLog();

  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const factoryInfo = [
    { code: 'SMP301', name: 'Factory 301', count: 6 },
    { code: 'SMP302', name: 'Factory 302', count: 75 },
    { code: 'SMP303', name: 'Factory 303', count: 35 },
    { code: 'SMP304', name: 'Factory 304', count: 22 },
    { code: 'SMP309', name: 'Factory 309', count: 41 },
    { code: 'SMP311', name: 'Factory 311', count: 89 },
    { code: 'SMP313', name: 'Factory 313', count: 89 },
    { code: 'SMP314', name: 'Factory 314', count: 8 },
    { code: 'SMP323', name: 'Factory 323', count: 37 },
    { code: 'SMP339', name: 'Factory 339', count: 1 }
  ];

  const onDrop = useCallback((acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(file =>
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    );

    if (pdfFiles.length !== acceptedFiles.length) {
      showNotification('Only PDF files are supported', 'warning');
    }

    if (pdfFiles.length === 0) return;

    const newFiles = pdfFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      status: 'ready',
      progress: 0
    }));

    setFiles(prev => [...prev, ...newFiles]);
  }, [showNotification]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  const removeFile = (id) => {
    setFiles(files => files.filter(f => f.id !== id));
  };

  const processFiles = async () => {
    if (files.length === 0) {
      setError('Please select files to process');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    // Generate unique activity process ID
    const activityProcessId = `amazon-label-${Date.now()}`;

    // Add to global activity log
    addActivityProcess(activityProcessId, `Label Separation (${files.length} files)`, 'Amazon DI UCC Label');
    addLog(activityProcessId, `Starting separation of ${files.length} PDF file(s)...`);

    const processId = addProcess({
      name: 'Amazon Label Separation',
      status: 'processing',
      progress: 0
    });

    try {
      const allSeparatedFiles = [];

      for (let i = 0; i < files.length; i++) {
        const fileData = files[i];
        const overallProgress = ((i / files.length) * 100);

        updateProcess(processId, {
          progress: overallProgress,
          status: `Processing ${fileData.name}...`
        });
        updateActivityProcess(activityProcessId, { progressPercentage: overallProgress });
        addLog(activityProcessId, `Processing: ${fileData.name}`);

        setFiles(prev => prev.map(f =>
          f.id === fileData.id ? { ...f, status: 'processing', progress: 0 } : f
        ));

        const formData = new FormData();
        formData.append('file', fileData.file);

        try {
          const result = await apiCall(API_ENDPOINTS.AMAZON_LABEL_SEPARATE, {
            method: 'POST',
            body: formData
          });

          setFiles(prev => prev.map(f =>
            f.id === fileData.id ? {
              ...f,
              status: 'completed',
              progress: 100,
              result: result.result
            } : f
          ));

          if (result.result.separatedFiles && result.result.separatedFiles.length > 0) {
            allSeparatedFiles.push(...result.result.separatedFiles);
            addLog(activityProcessId, `${fileData.name}: ${result.result.separatedFiles.length} factory file(s) created`);
            result.result.separatedFiles.forEach(sf => {
              addLog(activityProcessId, `  - ${sf.factoryName}: ${sf.fileName}`);
            });
          } else {
            addLog(activityProcessId, `${fileData.name}: No matching factory codes found`);
          }

        } catch (error) {
          console.error('Error processing file:', error);
          addLog(activityProcessId, `Error processing ${fileData.name}: ${error.message}`);
          setFiles(prev => prev.map(f =>
            f.id === fileData.id ? {
              ...f,
              status: 'error',
              error: error.message
            } : f
          ));
        }
      }

      updateProcess(processId, {
        status: 'completed',
        progress: 100
      });
      updateActivityProcess(activityProcessId, { progressPercentage: 100 });

      setResults(allSeparatedFiles);

      if (allSeparatedFiles.length > 0) {
        setSuccess(`Processing completed! ${allSeparatedFiles.length} factory files generated.`);
        addLog(activityProcessId, `Completed: ${allSeparatedFiles.length} factory file(s) ready for download`);
        completeActivityProcess(activityProcessId, true);
      } else {
        setSuccess('Processing completed. No matching factory codes found in the uploaded files.');
        addLog(activityProcessId, 'Completed: No matching factory codes found');
        completeActivityProcess(activityProcessId, true);
      }

      setTimeout(() => {
        removeProcess(processId);
      }, 3000);

    } catch (error) {
      console.error('Processing error:', error);
      updateProcess(processId, {
        status: 'error',
        progress: 0
      });
      addLog(activityProcessId, `Error: ${error.message}`);
      completeActivityProcess(activityProcessId, false);
      setError(`Processing failed: ${error.message}`);

      setTimeout(() => {
        removeProcess(processId);
      }, 5000);
    } finally {
      setProcessing(false);
    }
  };

  const downloadFile = async (downloadUrl, fileName) => {
    try {
      const encodedUrl = encodeURIComponent(downloadUrl);
      const response = await apiCall(API_ENDPOINTS.AMAZON_LABEL_DOWNLOAD(encodedUrl));

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('File downloaded successfully', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Download failed', 'error');
    }
  };

  const clearAll = () => {
    setFiles([]);
    setResults([]);
    setError('');
    setSuccess('');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Amazon DI UCC Label" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          AMAZON DI UCC LABEL
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Label Separation
              </Typography>

              {/* Factory Information Card */}
              <Card sx={{ mb: 3, bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                    Supported Factory Codes
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                    Labels will be automatically separated into these factory groups:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {factoryInfo.map((factory) => (
                      <Chip
                        key={factory.code}
                        icon={<Factory sx={{ fontSize: 14, color: '#DC143C' }} />}
                        label={`${factory.code} (${factory.count})`}
                        size="small"
                        sx={{
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(220, 20, 60, 0.15)' : 'rgba(220, 20, 60, 0.1)',
                          color: '#DC143C',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>

              {/* File Upload */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: '#DC143C' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Import PDF Files
                    </Typography>
                    {files.length > 0 && (
                      <Tooltip title={`${files.length} file(s) selected`}>
                        <Visibility sx={{ color: '#4CAF50' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Drag & Drop Zone - Clickable */}
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: `2px dashed ${isDragActive ? '#DC143C' : files.length > 0 ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: isDragActive
                        ? (theme.palette.mode === 'dark' ? 'rgba(220, 20, 60, 0.15)' : 'rgba(220, 20, 60, 0.06)')
                        : files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      mb: 2,
                      '&:hover': {
                        borderColor: files.length > 0 ? '#4CAF50' : '#DC143C',
                        bgcolor: files.length > 0
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
                          : (theme.palette.mode === 'dark' ? 'rgba(220, 20, 60, 0.2)' : 'rgba(220, 20, 60, 0.1)'),
                      }
                    }}
                  >
                    <input {...getInputProps()} />
                    {files.length === 0 ? (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDragActive ? '#DC143C' : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {isDragActive ? 'Drop PDF files here' : 'Drag & drop PDF files here or click to select'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supports multiple Amazon label PDF files
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                          {files.length} file(s) selected
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Click or drop more files to add
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* File List */}
                  {files.length > 0 && (
                    <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                      {files.map((fileData) => (
                        <Box
                          key={fileData.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 1,
                            mb: 1,
                            bgcolor: 'background.paper',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            <Description sx={{ color: '#DC143C', fontSize: 20 }} />
                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {fileData.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                              ({formatFileSize(fileData.size)})
                            </Typography>
                            {fileData.status === 'processing' && (
                              <Chip label="processing" size="small" color="primary" sx={{ ml: 1 }} />
                            )}
                            {fileData.status === 'completed' && (
                              <Chip label="completed" size="small" color="success" sx={{ ml: 1 }} />
                            )}
                            {fileData.status === 'error' && (
                              <Chip label="error" size="small" color="error" sx={{ ml: 1 }} />
                            )}
                          </Box>
                          {fileData.status === 'ready' && (
                            <IconButton
                              size="small"
                              onClick={() => removeFile(fileData.id)}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <Clear fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={processFiles}
                  disabled={files.length === 0 || processing}
                  startIcon={<FaCut />}
                  sx={{
                    bgcolor: '#DC143C',
                    '&:hover': { bgcolor: '#B71C1C' },
                    px: 4
                  }}
                >
                  {processing ? 'Separating Labels...' : 'Separate Labels'}
                </Button>

                {(files.length > 0 || results.length > 0) && (
                  <Button
                    variant="outlined"
                    onClick={clearAll}
                    disabled={processing}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Download Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    Separating labels by factory...
                  </Typography>
                  <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#DC143C' } }} />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              {/* Results Summary */}
              {results.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                    Separated Files ({results.length}):
                  </Typography>
                  <List dense>
                    {results.map((file, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          mb: 1,
                          p: 1,
                          bgcolor: theme.palette.background.paper
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => downloadFile(file.downloadUrl, file.fileName)}
                            sx={{ color: '#DC143C' }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Factory sx={{ color: '#DC143C' }} fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.factoryName}
                          secondary={file.fileName}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: theme.palette.text.primary }}
                          secondaryTypographyProps={{ variant: 'caption', color: theme.palette.text.secondary }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Download Section */}
              {results.length > 0 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Click the download icon next to each factory file to download.
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                How It Works:
              </Typography>
              <Typography variant="body2" component="div" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>1.</Box> Upload Amazon DI UCC label PDFs<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>2.</Box> System extracts ASINs from labels<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>3.</Box> Adds corresponding SKUs to each label<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>4.</Box> Separates by factory codes (SMP301-339)<br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>5.</Box> Download individual factory PDFs<br /><br />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>Note:</Box> Only factories with matching ASINs will generate output files.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AmazonLabelSeparator;
